import { Pencil, UserCheck, UserX } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { api, type AuthUser } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import { useAuth } from "../../../contexts/AuthContext.tsx";
import { useUsers } from "../../../hooks/resources.ts";
import ConfirmDialog from "../../../components/ConfirmDialog.tsx";
import FilterBar from "../../../components/filters/FilterBar.tsx";
import type { FilterSpec } from "../../../components/filters/specs.ts";
import { useFilters, useLocalFilterState } from "../../../components/filters/useFilters.ts";
import FormattedDate from "../../../components/FormattedDate";
import { UserCell } from "../../../components/IdentityCell.tsx";
import { RoleChip } from "../../../components/OrgRoles.tsx";
import PageHeader from "../../../components/PageHeader.tsx";
import RelativeTime from "../../../components/RelativeTime.tsx";
import RowActions from "../../../components/RowActions.tsx";
import Sparkline from "../../../components/Sparkline";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import TableCard from "../../../components/table/TableCard.tsx";
import { formatNumber, humanize } from "../../../utils/format.ts";

const ALL_ROLES = ["admin", "user", "publisher", "developer"] as const;
const COLUMN_COUNT = 8;

const userName = (u: AuthUser) => u.display_name || u.email;

function matches(u: AuthUser, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [u.display_name, u.email].some((s) => s?.toLowerCase().includes(q));
}

function UsersPage() {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [editingUser, setEditingUser] = useState<{
    id: string;
    name: string;
    roles: string[];
  } | null>(null);
  const [disableConfirm, setDisableConfirm] = useState<AuthUser | null>(null);

  const users = useUsers();

  const orgs = useQuery({
    queryKey: keys.organization.list(),
    queryFn: api.getOrganizations,
  });

  const specs = useMemo<FilterSpec[]>(
    () => [
      {
        kind: "options",
        key: "org",
        label: "Organization",
        param: "org",
        multiple: false,
        pinned: true,
        options: (orgs.data ?? []).map((o) => ({ value: o.id, label: o.name })),
        loading: orgs.isLoading,
        noneLabel: "All organizations",
        searchPlaceholder: "Search organizations",
      },
    ],
    [orgs.data, orgs.isLoading],
  );
  const filterState = useLocalFilterState();
  const filters = useFilters(specs, {
    state: filterState,
    search: { param: "q", placeholder: "Search name or email" },
  });
  const selectedOrgId = filters.get("org") ?? "";
  const search = filters.get("q") ?? "";

  const allUsers = users.data ?? [];
  const filteredUsers = allUsers.filter(
    (u) => (!selectedOrgId || u.organizations.some((o) => o.id === selectedOrgId)) && matches(u, search),
  );

  const mcpUsage = useQuery({
    queryKey: keys.mcpUsage.users14d(),
    queryFn: api.getMcpUsage14d,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { roles?: string[]; is_active?: boolean } }) =>
      api.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.user.all });
    },
  });

  const setActive = (target: AuthUser, isActive: boolean) =>
    updateMutation.mutateAsync(
      { id: target.id, data: { is_active: isActive } },
      { onSuccess: () => toast.success(isActive ? "User enabled" : "User disabled") },
    );

  const handleToggleActive = (target: AuthUser) => {
    if (target.is_active) {
      setDisableConfirm(target);
    } else {
      // A failure has already toasted; keep the rejection handled.
      setActive(target, true).catch(() => {});
    }
  };

  const confirmDisable = () => {
    if (disableConfirm) {
      updateMutation.mutate(
        { id: disableConfirm.id, data: { is_active: false } },
        { onSuccess: () => toast.success("User disabled") },
      );
      setDisableConfirm(null);
    }
  };

  const handleRoleToggle = (role: string) => {
    if (!editingUser) return;
    const roles = editingUser.roles.includes(role)
      ? editingUser.roles.filter((r) => r !== role)
      : [...editingUser.roles, role];
    setEditingUser({ ...editingUser, roles });
  };

  const handleSaveRoles = () => {
    if (!editingUser || editingUser.roles.length === 0) return;
    updateMutation.mutate(
      { id: editingUser.id, data: { roles: editingUser.roles } },
      {
        onSuccess: () => {
          setEditingUser(null);
          toast.success("Roles updated");
        },
      },
    );
  };

  const isSelf = (u: AuthUser) => u.id === currentUser?.id;

  const usersWord = (n: number) => (n === 1 ? "user" : "users");
  const count = !users.data
    ? undefined
    : filters.activeCount > 0
      ? `${formatNumber(filteredUsers.length)} of ${formatNumber(allUsers.length)} ${usersWord(allUsers.length)}`
      : `${formatNumber(allUsers.length)} ${usersWord(allUsers.length)}`;

  return (
    <>
      <PageHeader title="Users" description="Every account on the platform, across all organizations" />

      <TableCard loading={users.isLoading} toolbar={<FilterBar filters={filters} count={count} />}>
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>Organizations</TableCell>
              <TableCell>Active</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>MCP usage (14d)</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Last seen</TableCell>
              <TableCell>Joined</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((u) => (
              <TableRow key={u.id} hover sx={!u.is_active ? { opacity: 0.5 } : undefined}>
                <TableCell>
                  <UserCell
                    displayName={u.display_name}
                    email={u.email}
                    pictureUrl={u.picture_url}
                    tag={isSelf(u) ? "You" : undefined}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    {u.roles.map((r) => (
                      <RoleChip key={r} role={r} label={humanize(r)} />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  {u.organizations.length === 0 ? (
                    <Typography variant="body2" color="text.disabled">
                      —
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {u.organizations.map((o) => (
                        <Tooltip key={o.id} title={o.roles.join(", ")}>
                          <Chip label={o.name} size="small" variant="outlined" />
                        </Tooltip>
                      ))}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <Switch
                    checked={u.is_active}
                    onChange={() => handleToggleActive(u)}
                    disabled={isSelf(u) || updateMutation.isPending}
                    size="small"
                    slotProps={{ input: { "aria-label": `Active: ${userName(u)}` } }}
                  />
                </TableCell>
                <TableCell>
                  <Sparkline values={mcpUsage.data?.[u.id] ?? Array.from({ length: 14 }, () => 0)} />
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <RelativeTime value={u.last_seen} />
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>
                  <FormattedDate value={u.created_at} />
                </TableCell>
                <TableCell padding="checkbox" align="right">
                  <RowActions
                    label={userName(u)}
                    actions={[
                      {
                        label: "Edit roles",
                        icon: <Pencil fontSize="small" />,
                        onClick: () => setEditingUser({ id: u.id, name: userName(u), roles: [...u.roles] }),
                      },
                      {
                        label: "Enable user",
                        icon: <UserCheck fontSize="small" />,
                        hidden: u.is_active,
                        onClick: () => setActive(u, true),
                      },
                      {
                        label: "Disable user",
                        icon: <UserX fontSize="small" />,
                        destructive: true,
                        hidden: !u.is_active || isSelf(u),
                        onClick: () => setActive(u, false),
                        confirm: {
                          title: "Disable user",
                          confirmLabel: "Disable",
                          body: (
                            <>
                              Disable <strong>{userName(u)}</strong>? This will immediately log them out by
                              ending all their sessions.
                            </>
                          ),
                        },
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
            {filteredUsers.length === 0 && (
              <EmptyTableRow colSpan={COLUMN_COUNT}>
                {allUsers.length === 0 ? "No users yet" : "No users match your filters"}
              </EmptyTableRow>
            )}
          </TableBody>
        </ScrollTable>
      </TableCard>

      <ConfirmDialog
        open={!!disableConfirm}
        title="Disable user"
        confirmLabel="Disable"
        pending={updateMutation.isPending}
        onConfirm={confirmDisable}
        onClose={() => setDisableConfirm(null)}
      >
        Disable <strong>{disableConfirm && userName(disableConfirm)}</strong>? This will immediately log them
        out by ending all their sessions.
      </ConfirmDialog>

      {/* Edit roles dialog */}
      <Dialog open={!!editingUser} onClose={() => setEditingUser(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit roles: {editingUser?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {ALL_ROLES.map((role) => {
              const isOwnAdmin =
                editingUser &&
                isSelf({ id: editingUser.id } as AuthUser) &&
                role === "admin";
              return (
                <FormControlLabel
                  key={role}
                  control={
                    <Checkbox
                      checked={editingUser?.roles.includes(role) ?? false}
                      onChange={() => handleRoleToggle(role)}
                      disabled={!!isOwnAdmin}
                    />
                  }
                  label={humanize(role)}
                />
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingUser(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveRoles}
            loading={updateMutation.isPending}
            disabled={(editingUser?.roles.length ?? 0) === 0}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default UsersPage;
